import { useCallback, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'

import { useApi } from '../../../hooks'
import useAppContext from '../../app'
import toast from '../../toast'
import { resolveDocumentAction, toApiDocType } from '../../../utils/workflowDocuments'

// A card row is { ...vehicle, docID, docType }, so the vehicle's own _id disambiguates
// multi-vehicle documents.
export const cardKey = (item = {}) => `${item.docID}:${item._id}`

/**
 * Card click -> the documents behind that card. One document opens straight away; several
 * open a chooser. Only one lookup runs at a time, so a double click cannot fire two.
 */
const useRelatedDocuments = () => {
  const history = useHistory()
  const { workflowService } = useApi()
  const { store: { clientSettings } } = useAppContext()
  const canViewAdas = !!(clientSettings && clientSettings.vehicleReports && clientSettings.vehicleReports.allowView)

  const inFlight = useRef(false)
  const [openingKey, setOpeningKey] = useState(null)
  const [choices, setChoices] = useState(null)

  const openRelatedDocuments = useCallback(async (item) => {
    const docType = toApiDocType(item && item.docType)
    if (inFlight.current || !docType || !item.docID) return

    inFlight.current = true
    setOpeningKey(cardKey(item))
    try {
      const { documents } = await workflowService.getRelatedDocuments({
        docType,
        docId: item.docID,
        vehicleId: item._id,
      })
      const result = resolveDocumentAction(documents, canViewAdas)
      if (result.action === 'navigate') history.push(result.route)
      else if (result.action === 'choose') setChoices(result.documents)
      else toast.info({ title: 'No documents', body: 'There are no documents to open for this vehicle.' })
    } catch (err) {
      const notFound = err && err.message === 'API Not found'
      toast.error({
        title: 'Unable to open documents',
        body: notFound ? 'This document is no longer available.' : ((err && err.message) || 'Please try again.'),
      })
    } finally {
      inFlight.current = false
      setOpeningKey(null)
    }
  }, [workflowService, canViewAdas, history])

  const closeChooser = useCallback(() => setChoices(null), [])

  return { openRelatedDocuments, openingKey, choices, closeChooser }
}

export default useRelatedDocuments
